import { normalizeHtmlEntities } from "../../../api/helpers";
import "./css/SigningFlow.css";

export default function LastSignedFullWaiverScreen({
    flowData,
    updateFlowData,
    flowContext,
    updateFlowContext,
    next,
    back,
    loading,
    setLoading,
    error,
    setError,
}) {
    const snapshot =
        flowData?.lastSignedFullWaiver?.contentSnapshot;

    const waiver = snapshot?.waiver;
    const sections = waiver?.sections || [];
    const stateClauses = waiver?.stateClauses || [];

    const signer = snapshot?.signer;
    const eligibilitySnapshot = snapshot?.eligibilitySnapshot;
    const eligibilityQuestions = eligibilitySnapshot?.questions || [];
    const acknowledgments = snapshot?.acknowledgments || [];
    const signature = snapshot?.signature;
    const signedAt = snapshot?.signedAt;

    const goBack = () => {
        if (loading) return;

        next({
            action: "go_back"
        });
    };

    return (
        <div className={`page ${loading ? "page-loading" : ""}`}>
            
            {loading && (
                <div className="loading-overlay">
                    Loading...
                </div>
            )}

            {error && (
                <div className="text-red-600 mt-2">
                    {error}
                </div>
            )}
            
            <div className="waiver-header">
                <button
                    className="waiver-back"
                    onClick={goBack}
                    disabled={loading}
                >
                    ← Back
                </button>
            </div>

            <div className="page-content signature-content">

                <h2 className="signature-title">
                    Prior Signed Waiver
                </h2>

                
                {/*eligibility answers */}
                <div className="prior-waiver-divider" />
               
                {eligibilityQuestions.length > 0 && (
                    <>
                        <h3 className="signature-heading">
                            Eligibility Answers
                        </h3>

                        {eligibilityQuestions.map((question, index) => {
                            const selectedAnswers =
                                question?.selectedAnswers || [];

                            const selectedLabels =
                                selectedAnswers
                                    .map(answer => answer?.label)
                                    .filter(Boolean);

                            const selectedLabelSet = new Set(
                                selectedLabels.map(label =>
                                    label.trim().toLowerCase()
                                )
                            );

                            const options =
                                question?.options || [];

                            return (
                                <div
                                    key={
                                        question.id ||
                                        question._id ||
                                        index
                                    }
                                    className="final-acknowledgement"
                                >
                                    {/* Question */}
                                    <div className="signature-text">
                                        <strong>
                                            {question?.question ||
                                                `Question ${index + 1}`}
                                        </strong>
                                    </div>

                                    {/* Options */}
                                    {options.map((option, optionIndex) => {
                                        const label =
                                            option?.label || "";

                                        if (!label) {
                                            return null;
                                        }

                                        const isSelected =
                                            selectedLabelSet.has(
                                                label.trim().toLowerCase()
                                            );

                                        let optionText =
                                            `${isSelected ? "[X]" : "[ ]"} ${label}`;

                                        // Same "Other" handling as PDF
                                        if (
                                            isSelected &&
                                            label.trim().toLowerCase() === "other" &&
                                            question?.otherText
                                        ) {
                                            optionText +=
                                                ` — ${question.otherText}`;
                                        }

                                        return (
                                            <div
                                                key={optionIndex}
                                                className="signature-text"
                                            >
                                                {optionText}
                                            </div>
                                        );
                                    })}

                                    {/* Selected answer summary */}
                                    <div className="signature-text">
                                        <strong>
                                            Selected answer(s):
                                        </strong>{" "}
                                        {selectedLabels.length > 0
                                            ? selectedLabels
                                                .map(label => {
                                                    if (
                                                        label.trim().toLowerCase() ===
                                                            "other" &&
                                                        question?.otherText
                                                    ) {
                                                        return `Other — ${question.otherText}`;
                                                    }

                                                    return label;
                                                })
                                                .join(", ")
                                            : "None"}
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}

                {waiver?.title && (
                    <h3 className="signature-heading">
                        {normalizeHtmlEntities(waiver.title)}
                    </h3>
                )}

                {sections.map((section, index) => (
                    <div
                        key={index}
                        className="final-acknowledgement"
                    >
                        {section.title && (
                            <h3 className="signature-heading">
                                {normalizeHtmlEntities(
                                    section.title
                                )}
                            </h3>
                        )}

                        {section.content && (
                            <div
                                className="signature-text"
                                dangerouslySetInnerHTML={{
                                    __html:
                                        normalizeHtmlEntities(
                                            section.content
                                        )
                                }}
                            />
                        )}
                    </div>
                ))}

                {stateClauses.length > 0 && (
                    <>
                        <div className="prior-waiver-divider" />

                        {stateClauses.map((clause, index) => (
                            <div
                                key={index}
                                className="final-acknowledgement"
                            >
                                {clause.state && (
                                    <h3 className="signature-heading">
                                        {normalizeHtmlEntities(
                                            clause.state
                                        )}
                                    </h3>
                                )}

                                {clause.body && (
                                    <div
                                        className="signature-text"
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                normalizeHtmlEntities(
                                                    clause.body
                                                )
                                        }}
                                    />
                                )}
                            </div>
                        ))}
                    </>
                )}

                <div className="prior-waiver-divider" />

                <h3 className="signature-heading">
                    Signer
                </h3>

                <div className="signature-text">
                    <strong>Name:</strong>{" "}
                    {[signer?.firstName, signer?.lastName]
                        .filter(Boolean)
                        .join(" ")}
                </div>

                {signer?.email && (
                    <div className="signature-text">
                        <strong>Email:</strong> {signer.email}
                    </div>
                )}

                {signer?.phone && (
                    <div className="signature-text">
                        <strong>Phone:</strong> {signer.phone}
                    </div>
                )}


                <div className="prior-waiver-divider" />

                <h3 className="signature-heading">
                    Acknowledgments
                </h3>

                {acknowledgments.map((acknowledgment, index) => (
                    <div
                        key={acknowledgment.acknowledgmentId || index}
                        className="final-acknowledgement"
                    >
                        <div className="signature-text">
                            {normalizeHtmlEntities(
                                acknowledgment.text || ""
                            )}
                        </div>

                        {acknowledgment.response !== null &&
                            acknowledgment.response !== undefined && (
                                <div className="signature-text">
                                    <strong>Response:</strong>{" "}
                                    {String(acknowledgment.response)}
                                </div>
                            )}

                        {acknowledgment.acknowledgedAt && (
                            <div className="signature-text">
                                <strong>
                                    Acknowledged:
                                </strong>{" "}
                                {new Date(
                                    acknowledgment.acknowledgedAt
                                ).toLocaleString("en-US")}
                            </div>
                        )}
                    </div>
                ))}

                <div className="prior-waiver-divider" />

                <h3 className="signature-heading">
                    Signature
                </h3>

                
                {signature && (
                    <div className="typed-signature-preview">
                        {snapshot?.signatureMethod === "TYPE" ? (
                            <div className="typed-signature-box">
                                {snapshot?.signatureText ||
                                    `${signer?.firstName || ""} ${signer?.lastName || ""}`.trim()}
                            </div>
                        ) : (
                            typeof signature === "string" &&
                            signature.startsWith("data:image") ? (
                                <img
                                    src={signature}
                                    alt="Signed signature"
                                    className="signature-preview"
                                />
                            ) : (
                                signature
                            )
                        )}
                    </div>
                )}


                <div className="signature-text">
                    <strong>Signed:</strong>{" "}
                    {signedAt
                        ? new Date(signedAt).toLocaleString("en-US")
                        : ""}
                </div>

            </div>
        </div>
    );
}